/**
 * Space pricing (ADR 0058 — Phase 4e of the Designer Portal repo's
 * docs/PRICING_AND_INTERIORS_PLAN.md, plan §5/4e).
 *
 * Pure over the sketch MODEL: `priceSpace(model, rules)` walks every wall's
 * solved layout and stacks and prices what is actually designed —
 * catalog-anchored via the VENDORED shared `priceSection()` (plan §3), with
 * composition-delta adjustments derived from the catalog itself. No DOM, no
 * state of its own; node-tested against real createSketchModel() instances.
 *
 * Every price is DATA from the resolved business rules (plan §1.4 — the
 * admin-maintained tables win over the compiled-in fallback): the section
 * catalog, the material-multiplier curve, countertop rates, accessory prices,
 * the typeCode↔CV mapping, the garage platform charge, and the low/high
 * range percents. Nothing here states a dollar figure.
 *
 * The line model:
 *   - Each priced thing is a LINE with a low/high range and a BASIS badge:
 *     'exact' | 'interpolated' | 'extrapolated' | 'fallback' (all from
 *     priceSection), 'derived' (a composition delta or accessory rode on
 *     top), 'open' (the decided $0 for OPEN spacers), or 'needs-pricing'.
 *   - NEVER a silent zero: anything unmappable or unpriceable becomes a
 *     visible 'needs-pricing' line (the no-guessing rule). The ONE decided
 *     zero is OPEN (mapping row `zeroPrice`), badged 'open', panels still
 *     counted in the run math.
 *   - The material MULTIPLIER (level picked per space, default 1) applies to
 *     materialPrice only — nonMaterialPrice and accessory prices never scale
 *     (plan §1.2/§1.3; the wall-bed split mechanism, generalized).
 *   - The ballpark spread rides on top: low = ×rangeLowPercent/100,
 *     high = ×rangeHighPercent/100. Display rounding: $10 per line (wall
 *     lines included), $100 for the space total — ballpark conventions.
 *
 * Panel-run math (plan §1 Per Pricing + the owner's 2026-08-08 exception):
 *   - A run of N priced sections = N `Section +1P` prices + ONE closing `P`
 *     panel. Physical panels come from the layout solver (which already
 *     drops panels beside OPEN sections and doubles overridden joins), so
 *     extra panels to price = physical − one-per-non-OPEN-section.
 *   - A door/drawer section abutting a STRICTLY DEEPER neighbour needs its
 *     own full-depth panel at that boundary (detected from adjacent resolved
 *     depths) — priced as an extra `P` at the door/drawer section's own
 *     dims. A join the designer already doubled (fullPanelAfter) is not
 *     double-charged: the double join's extra panel IS the own panel.
 *   - Extra/closing panels are priced at the run's governing dims (tallest
 *     section's class + height, deepest section's depth): a shared panel
 *     must accommodate the largest thing it bounds.
 *   - `P` rows are exact-only in priceSection; a depth the catalog does not
 *     panel (the 14" default) takes a NEAREST-DEPTH-THEN-HEIGHT fallback
 *     implemented here for panels ONLY (same ordering and tie-break as
 *     priceSection's own fallback, never across classes), badged 'fallback'.
 *     Deliberate, documented deviation — ADR 0058.
 */
import { indexCatalog, priceSection } from './price-section.js';
import { deriveDoorGeometry, doorStyleByKey } from '../geometry/doors.js';
import { stackHasBaseTrim, stackHasCrownTrim } from '../geometry/trim.js';

const EPS = 1e-6;

/** Round to a step (nearest); 0/absent step returns the value untouched. */
export function roundToStep(value, step) {
  if (!Number.isFinite(value)) return value;
  if (!(step > 0)) return value;
  return Math.round(value / step) * step;
}

/**
 * The sketch's actual panel height → CV's nominal height code (ADR 0011 via
 * the mapping rule's heightAliases). Exact match only; anything else flows
 * through untouched and takes priceSection's badged nearest handling.
 */
export function cvHeightIn(actualUnits, mapping) {
  const aliases = Array.isArray(mapping?.heightAliases) ? mapping.heightAliases : [];
  for (const a of aliases) {
    if (Number.isFinite(a?.actualUnits) && Math.abs(actualUnits - a.actualUnits) < 0.01) return a.cvHeightIn;
  }
  return actualUnits;
}

/** The multiplier factor for a material level (default level 1 → 1). */
export function levelFactor(rules, level) {
  const levels = rules?.cvMaterialMultipliers?.levels;
  if (!Array.isArray(levels)) return 1;
  const row = levels.find((l) => l?.level === level);
  return Number.isFinite(row?.factor) && row.factor > 0 ? row.factor : 1;
}

/** rounds a float money figure to cents — keeps sums stable, display rounds later. */
const cents = (v) => Math.round(v * 100) / 100;

/**
 * The price DIFFERENCE between two configs at the section's dims, derived
 * from the catalog itself (plan §5/4e component deltas). Both configs are
 * priced at the SAME (height, depth) — candidate dims are the ones where the
 * catalog carries BOTH configs in this class, tried nearest-depth-first then
 * nearest-height (priceSection's own fallback ordering, ties to the smaller
 * size) so the delta never mixes two different carcasses.
 *
 * Returns { material, nonMaterial, anchors, note } or null when no dim pair
 * prices both configs (the caller shows needs-pricing, never guesses).
 */
export function configDelta(index, classId, heightIn, depthIn, widthIn, configHigh, configLow) {
  const dims = new Map(); // `${h}|${d}` -> {heightIn, depthIn, seenHigh, seenLow}
  for (const row of index.sections) {
    if (row.class !== classId) continue;
    if (row.config !== configHigh && row.config !== configLow) continue;
    const key = `${row.heightIn}|${row.depthIn}`;
    const entry = dims.get(key) ?? { heightIn: row.heightIn, depthIn: row.depthIn, seenHigh: false, seenLow: false };
    if (row.config === configHigh) entry.seenHigh = true;
    if (row.config === configLow) entry.seenLow = true;
    dims.set(key, entry);
  }
  const both = [...dims.values()].filter((e) => e.seenHigh && e.seenLow);
  both.sort((a, b) => {
    const dd = Math.abs(a.depthIn - (depthIn ?? 0)) - Math.abs(b.depthIn - (depthIn ?? 0));
    if (Math.abs(dd) > EPS) return dd;
    const dh = Math.abs(a.heightIn - (heightIn ?? 0)) - Math.abs(b.heightIn - (heightIn ?? 0));
    if (Math.abs(dh) > EPS) return dh;
    return (a.depthIn - b.depthIn) || (a.heightIn - b.heightIn);
  });
  for (const dim of both) {
    const hi = priceSection(index, { class: classId, heightIn: dim.heightIn, depthIn: dim.depthIn, widthIn, config: configHigh });
    const lo = priceSection(index, { class: classId, heightIn: dim.heightIn, depthIn: dim.depthIn, widthIn, config: configLow });
    if (!hi.ok || !lo.ok) continue;
    // A 'fallback' basis means priceSection wandered OFF the candidate dims
    // (its own nearest-size machinery) — the two sides would no longer be the
    // same carcass, so the difference would be dimension noise, not the
    // component's price. Try the next candidate instead.
    if (hi.basis === 'fallback' || lo.basis === 'fallback') continue;
    const exactDims = dim.heightIn === heightIn && dim.depthIn === depthIn;
    return {
      material: cents(hi.materialPrice - lo.materialPrice),
      nonMaterial: cents(hi.nonMaterialPrice - lo.nonMaterialPrice),
      anchors: [...hi.anchors, ...lo.anchors],
      note: exactDims ? null : `${configHigh}−${configLow} delta taken at ${dim.heightIn}"h × ${dim.depthIn}"d`,
    };
  }
  return null;
}

/**
 * A `P` panel at class/height/depth: exact catalog row when one exists, else
 * the panels-only nearest-depth-then-height fallback documented above.
 * Returns a priceSection-shaped result ({ok, materialPrice, ...}).
 */
export function pricePanel(index, classId, heightIn, depthIn) {
  const exact = priceSection(index, { class: classId, heightIn, depthIn, widthIn: null, config: 'P' });
  if (exact.ok) return exact;
  const rows = index.sections.filter((r) => r.class === classId && r.config === 'P');
  if (!rows.length) return { ok: false, reason: `no ${classId} panel rows in the catalog` };
  const sorted = [...rows].sort((a, b) => {
    const dd = Math.abs(a.depthIn - (depthIn ?? 0)) - Math.abs(b.depthIn - (depthIn ?? 0));
    if (Math.abs(dd) > EPS) return dd;
    const dh = Math.abs(a.heightIn - (heightIn ?? 0)) - Math.abs(b.heightIn - (heightIn ?? 0));
    if (Math.abs(dh) > EPS) return dh;
    return (a.depthIn - b.depthIn) || (a.heightIn - b.heightIn);
  });
  const row = sorted[0];
  return {
    ok: true,
    price: row.price,
    materialPrice: row.materialPrice,
    nonMaterialPrice: row.nonMaterialPrice,
    perPricing: row.perPricing,
    basis: 'fallback',
    anchors: [row.source],
    note: `no ${heightIn}"h × ${depthIn}"d panel priced; used nearest ${row.heightIn}"h × ${row.depthIn}"d`,
  };
}

// Basis severity for a line's overall badge: the worst component wins.
const BASIS_RANK = { exact: 0, open: 0, interpolated: 1, extrapolated: 2, fallback: 3, derived: 4, 'needs-pricing': 5 };
const worseBasis = (a, b) => (BASIS_RANK[b] > BASIS_RANK[a] ? b : a);

function accessoryByKey(rules, key) {
  return (Array.isArray(rules.cvAccessories) ? rules.cvAccessories : [])
    .find((a) => a?.key === key && Number.isFinite(a.price));
}

/** Element-level accessory attachments: string keys or {key} objects. */
function elementHasAccessory(el, key) {
  return (Array.isArray(el?.accessories) ? el.accessories : [])
    .some((a) => a === key || a?.key === key);
}

/**
 * Prices the whole space. Returns
 * {
 *   level, factor, countertopMaterial,
 *   walls: [{ wallId, label, lines, low, high, lowExact, highExact, needsPricing }],
 *   space: { low, high, lowExact, highExact, needsPricing },
 * }
 * Line: { label, basis, low, high, lowExact, highExact, anchors, notes }.
 * `low`/`high` everywhere are DISPLAY figures ($10 per line/wall, $100 for
 * the space); the *Exact twins are the unrounded sums the rounding is taken
 * from, so totals never accumulate rounding error.
 */
/**
 * How many panels a catalog row's price already includes, from its own
 * `perPricing` column (D110).
 *
 * The column has been carried through `priceSection`'s result since it existed
 * and was never read: the panel accounting assumed **one per section**. Four of
 * 145 rows disagree — the two wall beds include two, the two connecting shelves
 * include none — so a run with a wall bed was charged a panel that does not
 * exist, and a run with a connecting shelf was **credited one that does not
 * exist and under-charged**. The owner was told fixing the second moves prices
 * on designs already quoted, and approved it on that basis.
 *
 * An unstated value falls back to **1**, not 0: that is the historical
 * assumption, and a row that says nothing must keep behaving as it always has
 * rather than silently gaining a panel charge.
 *
 * @param {string} perPricing e.g. "Section +1P", "Section +2P", "Section"
 * @returns {number} panels already covered by the section's own price
 */
export function includedPanelsFor(perPricing) {
  const text = String(perPricing ?? '').trim();
  const plus = /\+\s*(\d+)\s*P$/i.exec(text);
  if (plus) return Number(plus[1]);
  if (/^section$/i.test(text)) return 0;
  return 1;
}

export function priceSpace(model, rules, opts = {}) {
  const mapping = rules.cvTypeMapping ?? {};
  const mapRows = new Map((Array.isArray(mapping.rows) ? mapping.rows : [])
    .map((r) => [String(r.code).toUpperCase(), r]));
  const index = indexCatalog(rules.cvSectionCatalog?.sections ?? []);
  const level = Number.isInteger(opts.level) && opts.level >= 1 ? opts.level : 1;
  const factor = levelFactor(rules, level);
  const lowPct = Number.isFinite(rules.pricingRangeLowPercent) ? rules.pricingRangeLowPercent : 100;
  const highPct = Number.isFinite(rules.pricingRangeHighPercent) ? rules.pricingRangeHighPercent : 125;
  const ctMaterialCode = typeof opts.countertopMaterial === 'string' && opts.countertopMaterial ? opts.countertopMaterial : null;
  const panelT = rules.sectionPanelThicknessUnits > 0 ? rules.sectionPanelThicknessUnits : 0.75;
  const openCodes = new Set((rules.sectionOpenCodes ?? []).map((c) => String(c).toUpperCase()));
  const presets = new Map((rules.sectionTypePresets ?? []).map((p) => [String(p.code).toUpperCase(), p]));
  const doorStyles = Array.isArray(rules.doorStyles) ? rules.doorStyles : [];

  // Host walls in a stable, wall-number order; partition faces after.
  const hostIds = [...new Set(model.state.sections.map((s) => s.wallId))];
  const wallOrder = new Map(model.state.walls.map((w, i) => [w.id, i]));
  hostIds.sort((a, b) => (wallOrder.get(a) ?? 1e9) - (wallOrder.get(b) ?? 1e9));

  const walls = [];
  for (const wallId of hostIds) {
    const layout = model.sectionLayoutForWall(wallId);
    const stacks = model.stacksForWall(wallId);
    if (!layout || !stacks.length) continue;
    const wall = model.state.walls.find((w) => w.id === wallId);
    const label = wall?.wallNumber ? `W${wall.wallNumber}` : 'Partition';
    const lines = [];
    let activeSectionGroup = null;
    // Panels already covered by the sections' own prices, SUMMED from each
    // priced row's `perPricing` rather than assumed one-per-section (D110).
    let includedPanels = 0;

    /** Accumulates one line from {material, nonMaterial} component pairs; a
     *  component may state separate low/high figures (hamper ranges). */
    const pushLine = (lineLabel, parts, basis, anchors, notes) => {
      let lowSum = 0;
      let highSum = 0;
      for (const p of parts) {
        const matLow = p.materialLow ?? p.material ?? 0;
        const matHigh = p.materialHigh ?? p.material ?? 0;
        const nonLow = p.nonMaterialLow ?? p.nonMaterial ?? 0;
        const nonHigh = p.nonMaterialHigh ?? p.nonMaterial ?? 0;
        lowSum += nonLow + matLow * factor;
        highSum += nonHigh + matHigh * factor;
      }
      const lowExact = cents(lowSum * (lowPct / 100));
      const highExact = cents(highSum * (highPct / 100));
      lines.push({
        label: lineLabel,
        sectionId: activeSectionGroup?.id ?? null,
        sectionLabel: activeSectionGroup?.label ?? null,
        basis,
        lowExact,
        highExact,
        low: roundToStep(lowExact, 10),
        high: roundToStep(highExact, 10),
        anchors: anchors ?? [],
        notes: (notes ?? []).filter(Boolean),
      });
    };
    const pushNeedsPricing = (lineLabel, reason) => {
      lines.push({
        label: lineLabel,
        sectionId: activeSectionGroup?.id ?? null,
        sectionLabel: activeSectionGroup?.label ?? null,
        basis: 'needs-pricing',
        lowExact: null,
        highExact: null,
        low: null,
        high: null,
        anchors: [],
        notes: [reason].filter(Boolean),
      });
    };

    // ---- sections (run + stacked units) ----------------------------------
    stacks.forEach((stack, i) => {
      const item = layout.items[i];
      if (!item) return;
      stack.units.forEach((unit, k) => {
        const section = unit.section;
        const code = String(section.typeCode ?? '').toUpperCase();
        const preset = presets.get(code);
        const name = preset?.name ?? code;
        const widthIn = k === 0 ? item.openingUnits : unit.widthUnits;
        const stackedTag = k > 0 ? ' (stacked)' : '';
        const lineLabel = `${name} ${fmtIn(widthIn)}″${stackedTag}`;
        activeSectionGroup = { id: section.id, label: lineLabel };

        const map = mapRows.get(code);
        if (!map) {
          pushNeedsPricing(lineLabel, `no CV mapping for type ${code} — needs mapping`);
          return;
        }
        if (map.zeroPrice === true || openCodes.has(code)) {
          // The ONE decided $0 (OPEN): a structural spacer. Its panels still
          // count — the layout solver already emits exactly the panels that
          // exist beside it, and the panel math below reads that layout.
          pushLine(lineLabel, [{ material: 0, nonMaterial: 0 }], 'open', [], ['open space — $0 by decision']);
          return;
        }

        const interior = model.sectionInterior(section) ?? [];
        const classId = k === 0 ? map.class : (mapping.stackedClass ?? 'ST');
        const solvedHeight = unit.topUnits - unit.bottomUnits;
        const heightIn = k === 0 && Number.isFinite(map.cvHeightIn)
          ? map.cvHeightIn
          : cvHeightIn(solvedHeight, mapping);
        const depthIn = unit.depthUnits;

        // Config resolution: shoe style / drawer count pick their row.
        let config = map.config;
        let expectedDrawers = 0;
        const faces = interior.reduce((n, el) => n + (el.kind === 'drawerBank' ? el.drawers.length : 0), 0);
        if (map.configByShoeStyle === true) {
          const shoe = interior.find((el) => el.kind === 'shoeShelves');
          if (shoe) config = shoe.style === 'FSS' ? 'FSS' : 'SSS';
        }
        if (map.configByDrawerCount && typeof map.configByDrawerCount === 'object') {
          const counts = Object.keys(map.configByDrawerCount).map(Number).filter(Number.isInteger).sort((a, b) => a - b);
          if (map.configByDrawerCount[faces]) {
            config = map.configByDrawerCount[faces];
            expectedDrawers = faces;
          } else if (counts.length && faces > counts[counts.length - 1]) {
            // Above the largest mapped bank: anchor at it, delta the rest.
            config = map.configByDrawerCount[counts[counts.length - 1]];
            expectedDrawers = counts[counts.length - 1];
          } else {
            // Below the smallest mapped bank (a 1–2 drawer DRW): no CV anchor
            // exists and no subtraction rule is decided — visible, not guessed.
            pushNeedsPricing(lineLabel, `no CV row for a ${faces}-drawer bank (smallest priced bank is ${counts[0] ?? '?'})`);
            return;
          }
        } else {
          expectedDrawers = Number.isInteger(mapping.configDrawerCounts?.[config]) ? mapping.configDrawerCounts[config] : 0;
        }

        // A STACKED unit always prices its DOOR-LESS carcass. Door faces use
        // the same covered-area mechanism as run sections below; choosing a
        // ShDr row here would make the identical face cost depend on stacking.
        // 2026-08-29): "stacked units are shelving sections … with or
        // without door" — ST|ShOp, never its own run-level config
        // (a stacked SH's FS, an LT95's Lsh, an AT95's AngSh have no ST
        // rows and describe the run product, not the stacked thing). Doors
        // The previous ShDr selection is deliberately gone: the style rate
        // replaces catalog door rows for both stacked and run sections.
        if (k > 0) {
          config = mapping.stackedConfig ?? 'ShOp';
          expectedDrawers = 0;
        }
        const carcass = priceSection(index, { class: classId, heightIn, depthIn, widthIn, config });
        if (!carcass.ok) {
          // No row, so no stated panel count — treat it as the historical
          // default of one, exactly as `includedPanelsFor` treats a row that
          // states nothing. Dropping to zero here would raise the panel charge
          // on a run that already cannot be quoted, changing a number for a
          // reason unrelated to panels.
          if (k === 0) includedPanels += 1;
          pushNeedsPricing(lineLabel, `${classId} ${config}: ${carcass.reason}`);
          return;
        }
        // Only a RUN section's own price covers the run's panels. A stacked
        // unit sits on the section below and adds no panel of its own to the
        // layout, so counting its row here would credit a panel that was never
        // physically there (D110).
        if (k === 0) includedPanels += includedPanelsFor(carcass.perPricing);
        const parts = [{ material: carcass.materialPrice, nonMaterial: carcass.nonMaterialPrice }];
        const anchors = [...carcass.anchors];
        const notes = [carcass.note];
        let basis = carcass.basis;
        if (map.interim === true) {
          notes.push(`interim mapping: ${code} prices as ${config} until CV adds ${code} rooms`);
          basis = worseBasis(basis, 'derived');
        }

        // Drawer-count difference from the mapped config: DRW-family deltas.
        const extraDrawers = faces - expectedDrawers;
        if (extraDrawers > 0) {
          const pair = config === 'DRW5'
            ? (mapping.extraDrawerDeltaPair ?? ['DRW5', 'DRW4'])
            : (mapping.perDrawerDeltaPair ?? ['DRW4', 'DRW3']);
          const per = configDelta(index, classId, heightIn, depthIn, widthIn, pair[0], pair[1]);
          if (per) {
            parts.push({ material: per.material * extraDrawers, nonMaterial: per.nonMaterial * extraDrawers });
            anchors.push(...per.anchors);
            notes.push(`${extraDrawers} extra drawer${extraDrawers > 1 ? 's' : ''} at the ${pair[0]}−${pair[1]} delta${per.note ? ` (${per.note})` : ''}`);
            basis = worseBasis(basis, 'derived');
          } else {
            pushNeedsPricing(`${lineLabel} — drawers`, `no ${pair[0]}/${pair[1]} pair priced in class ${classId} for the ${extraDrawers}-drawer difference`);
          }
        }

        // Elite toe stops on shoe shelves: count × accessory price.
        for (const el of interior) {
          if (el.kind !== 'shoeShelves' || !(el.count > 0)) continue;
          if (!elementHasAccessory(el, 'elite-toe-stop')) continue;
          const acc = accessoryByKey(rules, 'elite-toe-stop');
          if (acc) {
            parts.push({ material: 0, nonMaterial: cents(acc.price * el.count) });
            notes.push(`${el.count} Elite toe stops × $${acc.price}`);
            basis = worseBasis(basis, 'derived');
          } else {
            pushNeedsPricing(`${lineLabel} — toe stops`, 'elite-toe-stop accessory price missing from the accessory rule');
          }
        }
        // Lit shelves ride the same element-accessory mechanism ($180/shelf).
        for (const el of interior) {
          if (el.kind !== 'shelf' || !elementHasAccessory(el, 'lit-shelf')) continue;
          const acc = accessoryByKey(rules, 'lit-shelf');
          if (acc) {
            parts.push({ material: 0, nonMaterial: acc.price });
            notes.push(`lit shelf × $${acc.price}`);
            basis = worseBasis(basis, 'derived');
          } else {
            pushNeedsPricing(`${lineLabel} — lit shelf`, 'lit-shelf accessory price missing from the accessory rule');
          }
        }

        // Hamper sections: FS carcass + a hamper accessory RANGE across the
        // options at the section's width (null-width options offered at any).
        if (map.hamperAccessory === true) {
          const hampers = (Array.isArray(rules.cvAccessories) ? rules.cvAccessories : [])
            .filter((a) => a?.group === 'hamper' && Number.isFinite(a.price))
            .filter((a) => a.hamperWidthIn == null || Math.abs(a.hamperWidthIn - widthIn) < 0.51);
          if (hampers.length) {
            const prices = hampers.map((a) => a.price);
            parts.push({
              material: 0,
              nonMaterialLow: Math.min(...prices),
              nonMaterialHigh: Math.max(...prices),
            });
            notes.push(`hamper accessory range across ${hampers.length} option${hampers.length > 1 ? 's' : ''} at this width`);
            basis = worseBasis(basis, 'derived');
          } else {
            pushNeedsPricing(`${lineLabel} — hamper`, `no hamper accessory priced at ${fmtIn(widthIn)}″`);
          }
        }

        pushLine(lineLabel, parts, basis, anchors, notes);

        // Doors are their own line: covered extent × the selected STYLE rate.
        // Category is grouping only and is intentionally absent from the math.
        if (section.doors) {
          const feed = model.interiorForUnit(unit);
          const doors = feed?.doors ?? section.doors;
          const style = doorStyleByKey(doorStyles, doors?.styleKey);
          const geometry = deriveDoorGeometry(doors, feed?.solved?.elements, widthIn, style, rules);
          if (style && !(Number.isFinite(style.ratePerSqFt) && style.ratePerSqFt >= 0)) {
            pushNeedsPricing(`${lineLabel} — doors`, `door style ${style.key} has no resolvable square-foot rate`);
          } else if (!geometry.ok) {
            pushNeedsPricing(`${lineLabel} — doors`, geometry.reason);
          } else {
            const amount = cents(geometry.areaSqFt * style.ratePerSqFt);
            pushLine(
              `${lineLabel} — ${style.style} doors`,
              [{ material: 0, nonMaterial: amount }],
              'derived',
              [`door-style:${style.key}`],
              [`${geometry.areaSqFt.toFixed(2)} sq ft × $${style.ratePerSqFt}/sq ft; ${geometry.leaves.length} leaf column${geometry.leaves.length === 1 ? '' : 's'} × ${geometry.bands.length} height band${geometry.bands.length === 1 ? '' : 's'}`],
            );
          }
        }
      });
    });
    activeSectionGroup = null;

    // ---- the run's panels (plan §1 Per Pricing) --------------------------
    const runSections = stacks.map((s) => s.section);
    const nonOpen = runSections.filter((s) => !openCodes.has(String(s.typeCode).toUpperCase()));
    const physicalPanels = layout.panels.reduce((n, p) => n + Math.max(1, Math.round(p.widthUnits / panelT)), 0);
    // COUNTED from each priced row's own `perPricing`, not assumed as one per
    // section (D110). `includedPanels` was summed during the pricing loop
    // above, where each section's catalog row was already resolved — a section
    // that failed to price contributes nothing, which is correct: it has no
    // row, so it makes no claim about panels either.
    const extraPanels = Math.max(0, physicalPanels - includedPanels);
    if (extraPanels > 0 && nonOpen.length > 0) {
      // Governing dims: the tallest section's class + aliased height, the
      // deepest section's depth — a shared panel bounds the largest of them.
      const dims = stacks
        .filter((s, i) => layout.items[i] && !openCodes.has(String(s.section.typeCode).toUpperCase()))
        .map((s) => ({
          classId: mapRows.get(String(s.section.typeCode).toUpperCase())?.class ?? 'T',
          heightIn: cvHeightIn(s.units[0].topUnits - s.units[0].bottomUnits, mapping),
          depthIn: s.units[0].depthUnits,
        }));
      const tallest = dims.reduce((a, b) => (b.heightIn > a.heightIn ? b : a));
      const deepest = Math.max(...dims.map((d) => d.depthIn));
      const panel = pricePanel(index, tallest.classId, tallest.heightIn, deepest);
      if (panel.ok) {
        pushLine(
          `Panels ×${extraPanels}`,
          [{ material: panel.materialPrice * extraPanels, nonMaterial: panel.nonMaterialPrice * extraPanels }],
          panel.basis,
          panel.anchors,
          [panel.note, `${nonOpen.length} section price${nonOpen.length > 1 ? 's' : ''} include ${includedPanels} panel${includedPanels === 1 ? '' : 's'} between them; ${extraPanels} more physical`],
        );
      } else {
        pushNeedsPricing(`Panels ×${extraPanels}`, panel.reason);
      }
    }

    // The owner's 2026-08-08 exception: a door/drawer section abutting a
    // STRICTLY deeper neighbour needs its own full-depth panel there — unless
    // the designer already doubled that join (fullPanelAfter), which the
    // physical count above has priced.
    const hasDoorsOrDrawers = (section) => {
      if (section.doors) return true;
      const interior = model.sectionInterior(section) ?? [];
      return interior.some((el) => el.kind === 'drawerBank' && el.drawers.length > 0);
    };
    const depthOf = (s) => (s.depthUnits > 0 ? s.depthUnits : rules.sectionDefaultDepthUnits);
    runSections.forEach((section, i) => {
      const next = runSections[i + 1];
      if (!next) return;
      if (section.fullPanelAfter === true || model.isLShelf(section) || model.isLShelf(next)) return; // already a double join
      for (const [own, other] of [[section, next], [next, section]]) {
        if (!hasDoorsOrDrawers(own)) continue;
        if (!(depthOf(other) > depthOf(own) + EPS)) continue;
        const map = mapRows.get(String(own.typeCode).toUpperCase());
        const classId = map?.class ?? 'T';
        const h = cvHeightIn(model.sectionHeightUnits(own), mapping);
        const panel = pricePanel(index, classId, h, depthOf(own));
        const lbl = `Extra panel (${String(own.typeCode).toUpperCase()} beside deeper section)`;
        if (panel.ok) {
          pushLine(lbl, [{ material: panel.materialPrice, nonMaterial: panel.nonMaterialPrice }],
            worseBasis(panel.basis, 'derived'), panel.anchors,
            [panel.note, 'door/drawer section abutting a deeper neighbour needs its own full-depth panel']);
        } else {
          pushNeedsPricing(lbl, panel.reason);
        }
      }
    });

    // ---- garage platform charge ------------------------------------------
    if (model.state.platformHeightUnits > 0 && nonOpen.length > 0) {
      const charge = Number.isFinite(rules.garagePlatformChargePerSection) ? rules.garagePlatformChargePerSection : 0;
      pushLine(
        `Platform ×${nonOpen.length}`,
        [{ material: 0, nonMaterial: cents(charge * nonOpen.length) }],
        'exact',
        [],
        [`$${charge} per section on a platform (owner decision)`],
      );
    }

    // ---- crown and base trim, per LINEAR FOOT (owner, 2026-08-10) --------
    // "It should be based on linear ft. — put that as a maintenance table and
    // us $15 as the current default."
    //
    // MEASURED WALL TO WALL, deliberately. Trim really runs as front runs plus
    // side RETURNS, and the owner spelled the real rules out: returns at an
    // exposed end, crown wrapping the taller unit at a height change unless it
    // is ~3" deeper, base returning into the wall down the inside of any
    // section with no bottom shelf, and trim crossing connecting shelves to
    // die into the wall. Then:
    //
    //   "For purposes of ballparking (since we are assuming $15/foot) it may be
    //    easiest to ignore the returns and the complexities and just use the
    //    wall to wall distance as a proxy."
    //
    // So the wall's own length IS the measure. It already absorbs the corner
    // gaps and connecting shelves that a sum-of-section-widths would miss, and
    // it trades roughly evenly against the returns it ignores. NO waste
    // percentage rides on top: the proxy is the allowance, and a second fudge
    // over an approximation would be double-counting — raise $/ft instead.
    // The full ruleset is preserved in ADR 0070 for when this stops being a
    // ballpark.
    const trimWall = model.state.walls.find((w) => w.id === wallId);
    const wallRunUnits = trimWall ? model.wallLengthUnits(trimWall) : 0;
    const trimWidthOf = (section) => {
      const item = layout.items.find((it) => it.id === section.id);
      return item?.openingUnits > 0 ? item.openingUnits : 0;
    };
    const nonOpenStacks = stacks.filter((stack) =>
      !openCodes.has(String(stack.section.typeCode).toUpperCase()));
    const eligibleTrimUnits = (eligible) => {
      const skipped = nonOpenStacks.filter((stack) => !eligible(stack));
      return {
        skipped,
        units: skipped.length === nonOpenStacks.length
          ? 0
          : Math.max(0, wallRunUnits - skipped.reduce((sum, stack) => sum + trimWidthOf(stack.section), 0)),
      };
    };
    const pushTrim = (label, units, perFoot, extraNote) => {
      if (!(units > 0) || !(Number.isFinite(perFoot) && perFoot > 0)) return;
      const feet = units / 12;
      pushLine(
        label,
        [{ material: 0, nonMaterial: cents(perFoot * feet) }],
        'exact',
        [],
        [`${fmtIn(units)}″ = ${feet.toFixed(1)} linear ft at $${perFoot}/ft`, extraNote].filter(Boolean),
      );
    };
    // A wall only carries trim if something is built against it.
    if (nonOpen.length > 0) {
      if (model.state.crownEnabled === true) {
        // RS-16: "Base cabinets should never have crown, just base trim."
        // Eligibility is per footprint/stack, never inherited from the run.
        const crown = eligibleTrimUnits((stack) => stackHasCrownTrim(stack.units));
        pushTrim('Crown', crown.units, rules.crownPricePerLinearFoot,
          crown.skipped.length
            ? `wall to wall less ${fmtIn(wallRunUnits - crown.units)}″ — ${crown.skipped.length} base cabinet${crown.skipped.length > 1 ? 's' : ''} never get crown`
            : 'wall to wall');
      }
      if (model.state.baseTrimEnabled === true) {
        // The ONE complexity kept, because the owner asked for it explicitly
        // and each is a single subtraction rather than a returns model: "no
        // bottom shelf, no base trim" and RS-16's "Uppers have crown, no
        // base." An excluded footprint takes its own width out of the proxy.
        const base = eligibleTrimUnits((stack) => stackHasBaseTrim(stack.section, stack.units));
        const noShelf = base.skipped.filter((stack) => stack.section.bottomShelf === false).length;
        const uppers = base.skipped.length - noShelf;
        const reasons = [
          noShelf ? `${noShelf} section${noShelf > 1 ? 's' : ''} with no bottom shelf` : null,
          uppers ? `${uppers} upper${uppers > 1 ? 's' : ''} never get base trim` : null,
        ].filter(Boolean).join('; ');
        pushTrim('Base trim', base.units, rules.baseTrimPricePerLinearFoot,
          base.skipped.length
            ? `wall to wall less ${fmtIn(wallRunUnits - base.units)}″ — ${reasons}`
            : 'wall to wall');
      }
    }

    // ---- connecting shelves (corner bridges, ADR 0038) -------------------
    for (const shelf of model.connectingShelvesForWall(wallId)) {
      const boardCount = Number(shelf.topShelf !== false) + Number(shelf.bottomShelf !== false);
      const boardLabel = boardCount === 2 ? 'top + bottom' : (shelf.topShelf !== false ? 'top' : 'bottom');
      const cs = priceSection(index, { class: 'CS', heightIn: null, depthIn: shelf.depthUnits, widthIn: null, config: 'CS' });
      const lbl = `Connecting shelf ${fmtIn(shelf.depthUnits)}″d${boardCount ? ` · ${boardLabel}` : ' · open'}`;
      activeSectionGroup = { id: `connecting:${shelf.sectionId}:${shelf.end}`, label: `Connecting shelf · ${fmtIn(shelf.widthUnits)}″` };
      if (boardCount === 0) {
        pushLine(lbl, [{ material: 0, nonMaterial: 0 }], 'open', [], ['reserved open span; both connecting shelves are off']);
      } else if (cs.ok) {
        pushLine(lbl, [{ material: cs.materialPrice * boardCount, nonMaterial: cs.nonMaterialPrice * boardCount }], cs.basis, cs.anchors,
          [cs.note, `${boardCount} connecting shelf board${boardCount === 1 ? '' : 's'}`]);
      } else {
        pushNeedsPricing(lbl, `connecting shelves are priced per section at 12″/16″ depth only (${cs.reason})`);
      }
      activeSectionGroup = null;
    }

    // ---- countertops (contiguous top runs over bases, ADR 0041) ----------
    const anchors = runSections.map((s) => model.sectionAnchor(s));
    const hasTop = runSections.map((s, i) => anchors[i] === 'countertop'
      && (s.countertopThicknessUnits > 0 || model.state.countertopThicknessUnits > 0));
    let g = 0;
    while (g < runSections.length) {
      if (!hasTop[g]) { g += 1; continue; }
      let end = g;
      while (end + 1 < runSections.length && hasTop[end + 1]) end += 1;
      const first = layout.items[g];
      const last = layout.items[end];
      if (first && last) {
        const lengthIn = (last.startUnits + last.openingUnits) - first.startUnits;
        const depthIn = Math.max(...runSections.slice(g, end + 1).map(depthOf));
        const sqft = (lengthIn * depthIn) / 144;
        const lbl = `Countertop ${fmtIn(lengthIn)}″ × ${fmtIn(depthIn)}″`;
        const ct = rules.cvCountertops ?? {};
        const material = (Array.isArray(ct.materials) ? ct.materials : [])
          .find((m) => m?.materialCode === ctMaterialCode);
        if (!ctMaterialCode) {
          pushNeedsPricing(lbl, 'pick a countertop material in the Pricing panel');
        } else if (!material) {
          pushNeedsPricing(lbl, `countertop material ${ctMaterialCode} is not in the countertop rule`);
        } else {
          const fixed = Number.isFinite(ct.fixedChargePerCountertop?.price) ? ct.fixedChargePerCountertop.price : 0;
          // Countertops are separate from material LEVELS (plan §1.3): the
          // per-sq-ft rate is the material's own price — no multiplier.
          pushLine(lbl, [{ material: 0, nonMaterial: cents(sqft * material.perSqFt + fixed) }], 'exact',
            [material.code, ct.fixedChargePerCountertop?.source].filter(Boolean),
            [`${sqft.toFixed(2)} sq ft × $${material.perSqFt}/sq ft (${material.name}) + $${fixed} per top`]);
        }
      }
      g = end + 1;
    }

    // ---- wall totals ------------------------------------------------------
    const priced = lines.filter((l) => l.lowExact !== null);
    const lowExact = cents(priced.reduce((s, l) => s + l.lowExact, 0));
    const highExact = cents(priced.reduce((s, l) => s + l.highExact, 0));
    walls.push({
      wallId,
      label,
      lines,
      lowExact,
      highExact,
      low: roundToStep(lowExact, 10),
      high: roundToStep(highExact, 10),
      needsPricing: lines.filter((l) => l.basis === 'needs-pricing').length,
    });
  }

  const lowExact = cents(walls.reduce((s, w) => s + w.lowExact, 0));
  const highExact = cents(walls.reduce((s, w) => s + w.highExact, 0));
  return {
    level,
    factor,
    countertopMaterial: ctMaterialCode,
    walls,
    space: {
      lowExact,
      highExact,
      low: roundToStep(lowExact, 100),
      high: roundToStep(highExact, 100),
      needsPricing: walls.reduce((s, w) => s + w.needsPricing, 0),
    },
  };
}

/** Inches for a label: quarter-inch precision, no trailing zeros. */
function fmtIn(v) {
  if (!Number.isFinite(v)) return '?';
  const q = Math.round(v * 4) / 4;
  return Number.isInteger(q) ? String(q) : String(q);
}
